/**
 * Edição de Configurações de Chamadas de Entrada
 * VigosCloud - Incoming Call System
 * 
 * Formulário completo de edição baseado no InternalCallsEdit
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { 
  accessLocalStorage,
  getIncomingConfigById,
  updateIncomingConfig,
  fetchEndpoints,
  exportSelectAllQueues,
  getContextsByTenant,
  getContextsFilteredByTenant,
  getIntegrationsByAccountcode
} from '../../../services';
import { getAvailableTrunks } from '../../../services/incomingCallsApi';

import Loading from '../../Loading/LoadingModule';
import TrunkSelector from './components/TrunkSelector';
import TrunkSelectorOutbound from './components/TrunkSelectorOutbound';
import OverflowManager from './components/OverflowManager';
import TimeConditionManager from '../shared/TimeConditionManager';
import AudioConfiguration from './components/AudioConfiguration';
import AudioSelectorUploads from '../Audio/AudioSelectorUploads';
import RecordingConfiguration from './components/RecordingConfiguration';
import MohConfiguration from '../shared/MohConfiguration';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faArrowLeft, 
  faPhone,
  faEdit,
  faClock
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './IncomingCallsForm.css';

function IncomingCallsEdit() {
  const history = useHistory();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userTenant, setUserTenant] = useState('');
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [contextsFiltered, setContextsFiltered] = useState([]);
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [errors, setErrors] = useState({});

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [values, setValues] = useState({
    tenant: '',
    mask: '',
    trunk_name: '',
    destination_type_main: 'endpoint',
    destination_main: '',
    // Campos específicos para chamadas externas
    trunk_name_main: '',
    timeout_main: 60,
    // Campos específicos para DISA
    disa_context: '',
    disa_extension: 'menu',
    disa_priority: '1',
    recording_enabled: false,
    recording_format: 'wav',
    moh_enabled: false,
    moh_class: '',
    audio_enabled: false,
    audio_file: '',
    audio_type: 'ura',
    audio_accountcode: '',
    hangup_after_audio: false,
    description: '',
    active: true,
    overflows: [],
    gotoiftime_rules: []
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, [id]);

  // Limpar e configurar campos baseados no tipo de destino
  useEffect(() => {
    if (values.destination_type_main === 'hangup') {
      setValues(prev => ({ ...prev, destination_main: '' }));
    } else if (values.destination_type_main === 'whatsapp') {
      setValues(prev => ({ ...prev, destination_main: 'whatsapp' }));
    }
  }, [values.destination_type_main]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenant = user.user.idtenant;
      setUserTenant(tenant);

      // Carregar configuração existente
      const configResponse = await getIncomingConfigById(id);
      
      if (!configResponse.success) {
        showAlert('danger', 'Configuração não encontrada');
        setTimeout(() => {
          history.push('/config/chamadas-entrada');
        }, 2000);
        return;
      }

      // Preencher formulário com dados existentes
      setValues({
        ...configResponse.data,
        overflows: configResponse.data.overflows || [],
        gotoiftime_rules: configResponse.data.gotoiftime_rules || []
      });

      // Carregar dados auxiliares
      const [endpointsResult] = await Promise.all([
        fetchEndpoints()
      ]);

      if (endpointsResult?.success) {
        // Aplicar mesma lógica de filtro do MembersQueuesCreate
        let fetchedEndpoints = endpointsResult.data.map((element) => element).filter((element) => !isNaN(Number(element.endpoint)));
        fetchedEndpoints = tenant.includes('vgs') ? fetchedEndpoints : fetchedEndpoints.filter((endpoint) => tenant.includes(endpoint.tenant));
        
        setAvailableEndpoints(fetchedEndpoints);
        console.log('✅ [INCOMING-EDIT] Ramais carregados:', fetchedEndpoints.length);
      }

      await fetchQueues(tenant);
      await fetchContexts(tenant);
      await fetchContextsFiltered(tenant);
      await fetchTrunks(tenant);
      await checkWhatsAppIntegration(tenant);

    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao carregar dados:', error);
      showAlert('danger', 'Erro ao carregar dados da configuração');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueues = async (tenant) => {
    try {
      let queuesList = await exportSelectAllQueues();
      if (queuesList) {
        queuesList = tenant.includes('vgs') 
          ? queuesList 
          : queuesList.filter(q => tenant.includes(q.accountcodequeue));
        
        queuesList = queuesList.map(s => ({
          name: s.namequeue,
          displayname: s.descqueue || 'Sem descrição'
        }));
        
        setAvailableQueues(queuesList);
      }
    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao buscar filas:', error);
    }
  };

  const fetchContexts = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-EDIT] Buscando contextos DISA para tenant:', tenant);
      
      const response = await getContextsByTenant(tenant);
      console.log('📦 [INCOMING-EDIT] Resposta de contextos:', response);
      
      // Padronizar extração de dados como nas outras telas
      let contexts = [];
      if (Array.isArray(response)) {
        contexts = response;
      } else if (response && response.success && Array.isArray(response.data)) {
        contexts = response.data;
      } else if (response && response.contexts) {
        contexts = response.contexts;
      }
      
      setAvailableContexts(contexts);
      console.log('✅ [INCOMING-EDIT] Contextos carregados:', contexts.length);
    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao buscar contextos:', error);
      setAvailableContexts([]);
    }
  };

  const checkWhatsAppIntegration = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-EDIT] Verificando integrações WhatsApp para tenant:', tenant);
      
      const response = await getIntegrationsByAccountcode(tenant);
      
      if (response && response.success && response.data && response.data.length > 0) {
        setHasWhatsAppIntegration(true);
        console.log('✅ [INCOMING-EDIT] Integrações WhatsApp encontradas:', response.data.length);
      } else {
        setHasWhatsAppIntegration(false);
        console.log('⚠️ [INCOMING-EDIT] Nenhuma integração WhatsApp encontrada');
      }
    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  const fetchContextsFiltered = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-EDIT] Buscando contextos filtrados para tenant:', tenant);
      
      const response = await getContextsFilteredByTenant(tenant);
      
      if (response && response.success && response.data) {
        setContextsFiltered(response.data);
        console.log('✅ [INCOMING-EDIT] Contextos filtrados carregados:', response.data.length);
      } else {
        console.error('❌ [INCOMING-EDIT] Erro ao buscar contextos filtrados:', response.error);
        setContextsFiltered([]);
      }
    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao buscar contextos filtrados:', error);
      setContextsFiltered([]);
    }
  };

  // Carregar troncos disponíveis
  const fetchTrunks = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-EDIT] Buscando troncos para tenant:', tenant);
      
      const response = await getAvailableTrunks(tenant);
      
      if (response && response.success && Array.isArray(response.data)) {
        const filteredTrunks = response.data.filter(trunk => 
          trunk.accountcode === tenant || trunk.tenant === tenant
        );
        
        setAvailableTrunks(filteredTrunks);
        console.log('✅ [INCOMING-EDIT] Troncos carregados:', filteredTrunks.length);
      } else {
        setAvailableTrunks([]);
        console.log('⚠️ [INCOMING-EDIT] Nenhum tronco encontrado');
      }
    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao buscar troncos:', error);
      setAvailableTrunks([]);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const handleAudioConfigChange = (audioConfig) => {
    setValues(prev => ({ ...prev, ...audioConfig }));
  };

  const handleRecordingConfigChange = (recordingConfig) => {
    setValues(prev => ({ ...prev, ...recordingConfig }));
  };

  // Gerenciar Overflows
  const addOverflow = () => {
    const newOverflow = {
      sequence_order: values.overflows.length + 1,
      timeout_seconds: 30,
      destination_overflow: '',
      destination_type: 'endpoint',
      audio_enabled: false,
      audio_file: '',
      audio_type: 'ura',
      audio_accountcode: userTenant,
      hangup_after_audio: false,
      active: true
    };
    
    setValues(prev => ({
      ...prev,
      overflows: [...prev.overflows, newOverflow]
    }));
  };

  const removeOverflow = (index) => {
    setValues(prev => ({
      ...prev,
      overflows: prev.overflows.filter((_, i) => i !== index)
    }));
  };

  const updateOverflow = (index, field, value) => {
    setValues(prev => ({
      ...prev,
      overflows: prev.overflows.map((overflow, i) => {
        if (i !== index) return overflow;
        
        const updatedOverflow = { ...overflow, [field]: value };
        
        // Se mudou o tipo de destino para WhatsApp, setar destination_overflow automaticamente
        if (field === 'destination_type' && value === 'whatsapp') {
          updatedOverflow.destination_overflow = 'whatsapp';
        }
        
        // Se mudou o tipo de destino para Ciclo, setar destination_overflow automaticamente
        if (field === 'destination_type' && value === 'cycle') {
          updatedOverflow.destination_overflow = values.mask || 'CYCLE_RESTART';
        }
        
        // Se mudou de cycle para outro tipo, limpar CYCLE_RESTART
        if (field === 'destination_type' && value !== 'cycle' && overflow.destination_overflow === 'CYCLE_RESTART') {
          updatedOverflow.destination_overflow = '';
        }
        
        return updatedOverflow;
      })
    }));
  };

  // Gerenciar Time-Conditions
  const addGotoIfTimeRule = () => {
    const newRule = {
      sequence_order: values.gotoiftime_rules.length + 1,
      time_range: '08:00-18:00',
      days_of_week: '1-5',
      days_of_month: '*',
      months: '*',
      destination: '',
      destination_type: 'context',
      context_name: '',
      extension_name: '',
      priority: 1,
      description: '',
      active: true
    };
    
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: [...prev.gotoiftime_rules, newRule]
    }));
  };

  const removeGotoIfTimeRule = (index) => {
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: prev.gotoiftime_rules.filter((_, i) => i !== index)
    }));
  };

  const updateGotoIfTimeRule = (index, field, value) => {
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: prev.gotoiftime_rules.map((rule, i) => 
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!values.mask?.trim()) {
      newErrors.mask = 'Máscara de discagem é obrigatória';
    }

    if (!values.trunk_name?.trim()) {
      newErrors.trunk_name = 'Tronco é obrigatório';
    }

    if (values.destination_type_main !== 'endpoint' && 
        values.destination_type_main !== 'hangup' && 
        values.destination_type_main !== 'whatsapp' &&
        !values.destination_main?.trim()) {
      newErrors.destination_main = 'Destino principal é obrigatório';
    }

    if (values.destination_type_main === 'whatsapp' && !hasWhatsAppIntegration) {
      newErrors.destination_type_main = 'Nenhuma integração WhatsApp cadastrada para este tenant';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Scroll para o topo da página para mostrar o alert
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showAlert('danger', 'Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setSaving(true);

      console.log('🔍 [INCOMING-EDIT] Atualizando configuração:', values);
      console.log('🔍 [INCOMING-EDIT] Número de overflows:', values.overflows?.length || 0);
      
      // Validar overflows antes de enviar
      if (values.overflows && values.overflows.length > 0) {
        values.overflows.forEach((overflow, index) => {
          console.log(`🔍 [INCOMING-EDIT] Overflow ${index + 1}:`, {
            sequence_order: overflow.sequence_order,
            timeout_seconds: overflow.timeout_seconds,
            destination_type: overflow.destination_type,
            destination_overflow: overflow.destination_overflow,
            audio_enabled: overflow.audio_enabled
          });
        });
      }

      // Processar dados antes de enviar (mesmo que no componente de criação)
      const processedData = {
        mask: values.mask,
        trunk_name: values.trunk_name,
        destination_main: values.destination_main || null,
        destination_type_main: values.destination_type_main,
        recording_enabled: values.recording_enabled,
        recording_format: values.recording_format,
        moh_enabled: values.moh_enabled || false,
        moh_class: values.moh_class || null,
        audio_enabled: values.audio_enabled,
        audio_file: values.audio_file || null,
        audio_type: values.audio_type || null,
        audio_accountcode: values.audio_accountcode || null,
        hangup_after_audio: values.hangup_after_audio,
        description: values.description || null,
        active: values.active,
        overflows: values.overflows,
        gotoiftime_rules: values.gotoiftime_rules
      };

      // Processar destino principal baseado no tipo
      if (values.destination_type_main === 'external' && values.trunk_name_main) {
        processedData.destination_main = `${values.destination_main}@${values.trunk_name_main}`;
      } else if (values.destination_type_main === 'disa' && values.disa_context) {
        processedData.destination_main = `${values.disa_context},${values.disa_extension || 'menu'},${values.disa_priority || '1'}`;
      }
      
      // Processar overflows para concatenar campos compostos
      if (processedData.overflows && processedData.overflows.length > 0) {
        processedData.overflows = processedData.overflows.map(overflow => {
          const processedOverflow = { ...overflow };
          
          if (overflow.destination_type === 'external' && overflow.trunk_name) {
            processedOverflow.destination_overflow = `${overflow.destination_overflow}@${overflow.trunk_name}`;
          } else if (overflow.destination_type === 'disa' && overflow.disa_context) {
            processedOverflow.destination_overflow = `${overflow.disa_context},${overflow.disa_extension || 'menu'},${overflow.disa_priority || '1'}`;
          }
          
          return processedOverflow;
        });
      }

      console.log('🔍 [INCOMING-EDIT] Dados processados para envio:', processedData);
      console.log('🔍 [INCOMING-EDIT] Overflows a serem salvos:', processedData.overflows);

      const response = await updateIncomingConfig(id, processedData);

      if (response.success) {
        console.log('✅ [INCOMING-EDIT] Configuração atualizada com sucesso');
        showAlert('success', 'Configuração atualizada com sucesso!');
        setTimeout(() => {
          history.push('/config/chamadas-entrada');
        }, 2000);
      } else {
        console.error('❌ [INCOMING-EDIT] Erro ao atualizar:', response.error);
        if (response.details) {
          console.error('❌ [INCOMING-EDIT] Detalhes do erro:', response.details);
        }
        showAlert('danger', `Erro ao atualizar configuração: ${response.error}`);
      }

    } catch (error) {
      console.error('❌ [INCOMING-EDIT] Erro ao atualizar:', error);
      console.error('❌ [INCOMING-EDIT] Stack trace:', error.stack);
      showAlert('danger', 'Erro ao atualizar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="message-list-container">
      <div>
        <form onSubmit={handleSubmit}>
          
          {/* Cabeçalho */}
          <div className="message-list-header">
            <div className="message-list-title-wrapper">
              <div className="message-list-title-left">
                <svg className="message-list-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <h1 className="message-list-title">Editar Chamada de Entrada</h1>
                  <p className="message-list-subtitle">Ajuste as regras e destinos desta configuração</p>
                </div>
              </div>
              <div className="message-list-actions">
                <Link to="/config/chamadas-entrada" className="btn-new-message" style={{ background: '#e5e7eb', color: '#2c3e50' }}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Voltar
                </Link>
                <button type="submit" className="btn-new-message" style={{ background: 'linear-gradient(135deg, #0D75BA 0%, #1A8BCC 100%)' }} disabled={saving}>
                  <FontAwesomeIcon icon={faSave} />
                  Salvar Configuração
                </button>
              </div>
            </div>
          </div>

          {/* Alert */}
          {alert.show && (
            <div className={`notification is-${alert.type}`}>
              <button className="delete" onClick={() => setAlert({ ...alert, show: false })}></button>
              <div style={{ whiteSpace: 'pre-line' }}>{alert.message}</div>
            </div>
          )}

          {/* Informações Básicas */}
          <div className="box">
            <h3 className="title is-5">Informações Básicas</h3>
            
            <div className="columns">
              <div className="column is-6">
                <div className="field">
                  <label className="label">
                    Máscara DID *
                  </label>
                  <div className="control">
                    <input
                      type="text"
                      className={`input ${errors.mask ? 'is-danger' : ''}`}
                      value={values.mask}
                      onChange={(e) => handleInputChange('mask', e.target.value)}
                      placeholder="Ex: 1234, _11NXXXXXXX"
                    />
                  </div>
                  {errors.mask && <p className="help is-danger">{errors.mask}</p>}
                </div>
              </div>

              <div className="column is-6">
                <TrunkSelector
                  selectedTrunk={values.trunk_name}
                  onTrunkChange={(value) => handleInputChange('trunk_name', value)}
                  tenant={values.tenant}
                  error={errors.trunk_name}
                  required={true}
                />
              </div>
            </div>

            <div className="field">
              <label className="label">Descrição (opcional)</label>
              <div className="control">
                <input
                  type="text"
                  className="input"
                  value={values.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Ex: DID principal - Atendimento"
                />
              </div>
            </div>
          </div>

          {/* Destino Principal */}
          <div className="box">
            <h3 className="title is-5">
              <FontAwesomeIcon icon={faPhone} className="mr-2" />
              Destino Principal
            </h3>

            <div className="columns">
              <div className="column is-4">
                <div className="field">
                  <label className="label">Tipo de Destino *</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={values.destination_type_main}
                        onChange={(e) => handleInputChange('destination_type_main', e.target.value)}
                      >
                        <option value="endpoint">Ramal</option>
                        <option value="queue">Fila</option>
                        <option value="callcenter-v2">Callcenter V2</option>
                        <option value="external">Externo</option>
                        <option value="disa">DISA</option>
                        <option value="whatsapp" disabled={!hasWhatsAppIntegration}>
                          WhatsApp {!hasWhatsAppIntegration ? '(sem integração)' : ''}
                        </option>
                        <option value="hangup">Desligar</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="column is-8">
                {values.destination_type_main === 'endpoint' && (
                  <div className="field">
                    <label className="label">Ramal (4 dígitos numéricos)</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                        type="text" 
                        placeholder="Ex: 1000, 1001, 1234"
                        value={values.destination_main}
                        onChange={(e) => {
                          // Permitir apenas números e limitar a 4 dígitos
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          handleInputChange('destination_main', value);
                        }}
                        maxLength={4}
                      />
                    </div>
                    <p className="help">Digite o número do ramal (4 dígitos). Deixe vazio para usar o DID recebido ({'${EXTEN}'})</p>
                    {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                  </div>
                )}

                {values.destination_type_main === 'queue' && (
                  <div className="field">
                    <label className="label">Fila *</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={values.destination_main}
                          onChange={(e) => handleInputChange('destination_main', e.target.value)}
                          className={errors.destination_main ? 'is-danger' : ''}
                        >
                          <option value="">Selecione uma fila</option>
                          {availableQueues.map(queue => (
                            <option key={queue.name} value={queue.name}>
                              {queue.name} - {queue.displayname}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                  </div>
                )}

                {values.destination_type_main === 'callcenter-v2' && (
                  <div className="field">
                    <label className="label">Fila Callcenter V2 *</label>
                    <div className="control">
                      <input
                        className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                        type="text"
                        placeholder="Ex: suporte-mlz, vendas-mlz"
                        value={values.destination_main}
                        onChange={(e) => handleInputChange('destination_main', e.target.value)}
                      />
                    </div>
                    <p className="help">Digite o nome da fila do Callcenter V2. A chamada sera roteada via Stasis.</p>
                    {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                  </div>
                )}

                {/* Campo Timeout para endpoint, queue e external */}
                {(values.destination_type_main === 'endpoint' || 
                  values.destination_type_main === 'queue' || 
                  values.destination_type_main === 'external') && (
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faClock} className="mr-2" />
                      Timeout (segundos) {values.destination_type_main !== 'queue' && '*'}
                    </label>
                    <div className="control">
                      <input 
                        className={`input ${errors.timeout_main ? 'is-danger' : ''}`}
                        type="number" 
                        min="1"
                        max="300"
                        placeholder={
                          values.destination_type_main === 'queue' 
                            ? (values.overflows && values.overflows.length > 0 ? '15' : 'Sem timeout (indefinido)')
                            : (values.overflows && values.overflows.length > 0 ? '20' : '60')
                        }
                        value={values.timeout_main}
                        onChange={(e) => handleInputChange('timeout_main', parseInt(e.target.value) || 60)}
                      />
                    </div>
                    {errors.timeout_main && <p className="help is-danger">{errors.timeout_main}</p>}
                    {values.destination_type_main === 'endpoint' && (
                      <p className="help">Tempo limite de toque para o ramal (1-300 segundos).</p>
                    )}
                    {values.destination_type_main === 'queue' && (
                      <p className="help">
                        Tempo máximo na fila antes do overflow. 
                        {values.overflows && values.overflows.length > 0 
                          ? ' Padrão: 15 segundos.' 
                          : ' Deixe vazio para aguardar indefinidamente até um atendente responder.'}
                      </p>
                    )}
                    {values.destination_type_main === 'external' && (
                      <p className="help">Tempo limite para a chamada externa (1-300 segundos).</p>
                    )}
                  </div>
                )}

                {values.destination_type_main === 'external' && (
                  <>
                    <div className="field">
                      <label className="label">Número Externo *</label>
                      <div className="control">
                        <input
                          type="text"
                          className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                          value={values.destination_main}
                          onChange={(e) => handleInputChange('destination_main', e.target.value)}
                          placeholder="Ex: 11987654321"
                        />
                      </div>
                      {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                    </div>

                    <div className="field">
                      <label className="label">Tronco *</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select
                            value={values.trunk_name_main}
                            onChange={(e) => handleInputChange('trunk_name_main', e.target.value)}
                            className={errors.trunk_name_main ? 'is-danger' : ''}
                          >
                            <option value="">Selecione o tronco</option>
                            {availableTrunks.map(trunk => (
                              <option key={trunk.name} value={trunk.name}>
                                {trunk.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {errors.trunk_name_main && <p className="help is-danger">{errors.trunk_name_main}</p>}
                    </div>
                  </>
                )}

                {values.destination_type_main === 'disa' && (
                  <div className="field">
                    <label className="label">Contexto DISA *</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          className={errors.disa_context ? 'is-danger' : ''}
                          value={values.disa_context || ''}
                          onChange={(e) => handleInputChange('disa_context', e.target.value)}
                        >
                          <option value="">Selecione um contexto</option>
                          {availableContexts.filter(context => {
                            const contextName = (context.name || '').toLowerCase();
                            return contextName.includes('ura-') || contextName.includes('msg-');
                          }).map(context => (
                            <option key={context.name} value={context.name}>
                              {context.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {errors.disa_context && <p className="help is-danger">{errors.disa_context}</p>}
                    <p className="help">Selecione o contexto DISA/URA para esta chamada</p>
                  </div>
                )}

                {values.destination_type_main === 'whatsapp' && (
                  <div className="field">
                    <label className="label">Destino WhatsApp</label>
                    <div className="control">
                      <input
                        type="text"
                        className="input"
                        value="WhatsApp AGI (gerenciado automaticamente)"
                        disabled
                      />
                    </div>
                    <p className="help">
                      🟢 A chamada será encaminhada automaticamente para WhatsApp via AGI
                      {!hasWhatsAppIntegration && <span className="has-text-danger"> - Nenhuma integração WhatsApp cadastrada.</span>}
                    </p>
                  </div>
                )}

                {values.destination_type_main === 'hangup' && (
                  <div className="notification is-warning is-light">
                    A chamada será desligada imediatamente
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Gravação e Áudio lado a lado */}
          <div className="columns" style={{ alignItems: 'stretch' }}>
            <div className="column is-6" style={{ display: 'flex' }}>
              {/* Gravação */}
              <div style={{ width: '100%' }}>
                <RecordingConfiguration
                  config={{
                    recording_enabled: values.recording_enabled,
                    recording_format: values.recording_format
                  }}
                  onChange={handleRecordingConfigChange}
                />
              </div>
            </div>
            
            <div className="column is-6" style={{ display: 'flex' }}>
              {/* Áudio */}
              <div style={{ width: '100%' }}>
                <AudioConfiguration
                  config={{
                    audio_enabled: values.audio_enabled,
                    audio_file: values.audio_file,
                    audio_type: values.audio_type,
                    audio_accountcode: values.audio_accountcode,
                    hangup_after_audio: values.hangup_after_audio
                  }}
                  onChange={handleAudioConfigChange}
                  tenant={values.tenant}
                  showHangupOption={true}
                />
              </div>
            </div>
          </div>

          {/* Music on Hold */}
          <div className="columns mt-4">
            <div className="column">
              <MohConfiguration
                config={{
                  moh_enabled: values.moh_enabled || false,
                  moh_class: values.moh_class || ''
                }}
                tenant={values.tenant}
                onChange={(mohConfig) => {
                  setValues(prev => ({
                    ...prev,
                    moh_enabled: mohConfig.moh_enabled,
                    moh_class: mohConfig.moh_class
                  }));
                }}
                disabled={saving}
              />
            </div>
          </div>

          {/* Overflows */}
          <OverflowManager
            overflows={values.overflows}
            onAdd={addOverflow}
            onRemove={removeOverflow}
            onUpdate={updateOverflow}
            availableEndpoints={availableEndpoints}
            availableQueues={availableQueues}
            availableContexts={availableContexts}
            availableTrunks={availableTrunks}
            tenant={values.tenant}
            hasWhatsAppIntegration={hasWhatsAppIntegration}
          />

          {/* Time-Conditions */}
          <TimeConditionManager
            rules={values.gotoiftime_rules}
            onChange={(newRules) => setValues(prev => ({ ...prev, gotoiftime_rules: newRules }))}
            availableContexts={contextsFiltered}
            disabled={saving}
            title="Desvios por Data/Hora"
          />

          {/* Status */}
          <div className="box">
            <div className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={values.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
                <span className="ml-2">Configuração ativa</span>
              </label>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="level">
            <div className="level-left"></div>
            <div className="level-right">
              <div className="level-item">
                <div className="buttons">
                  <Link
                    to="/config/chamadas-entrada"
                    className="button is-light"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Cancelar
                  </Link>
                  <button
                    type="submit"
                    className={`button is-primary ${saving ? 'is-loading' : ''}`}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Salvar Configuração
                  </button>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}

export default IncomingCallsEdit;

